import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { InView } from 'react-intersection-observer';
import withEndlessScroll from '../../HOC/endlessScroll';
import { isLeasedCategory, setSuffix } from '../../../utils/helpers';
import { setError } from '../../../actions';
import { getAgencyFilterDataQuery } from '../../../queries';
import Button from '../../Button';
import AgencyFilterResultsList from './AgencyFilterResultsList';
import ItemCardPlaceholder from '../../Placeholders/ItemCardPlaceholder';
import ItemCard from '../../Cards/ItemCard';

const filterResultsBlockRef = React.createRef();
const EndlessSearchItems = withEndlessScroll(AgencyFilterResultsList);

const AgencyFilterResultsBlock = memo(
  ({
    title,
    category,
    agencyId,
    activeCategoryId,
    isLeased,
    limit,
    onClickItem,
    setActiveLeasedCategory
  }) => {
    const [itemId, setItemId] = useState(null);
    const [skipRequest, setSkipRequest] = useState(true);
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const setCarousel = id => {
      return itemId === id ? null : setItemId(id);
    };
    const showAllResultHandler = () => {
      if (activeCategoryId === 'all') {
        onClickItem(category.id);
      } else {
        onClickItem(category.id, false);
        setActiveLeasedCategory([activeCategoryId]);
      }
    };
    const getFilter = () => {
      const filterBy =
        activeCategoryId === 'all' || isLeased ? category.id : activeCategoryId;

      if (isLeased) {
        return {
          agencyId,
          categoryId: category.id,
          leased: activeCategoryId === 'lease'
        };
      }

      if (isLeasedCategory(category.id)) {
        return {
          agencyId,
          leased: category.id === 'lease'
        };
      }

      return {
        agencyId,
        categoryId: filterBy
      };
    };
    const { loading, data, error, fetchMore } = useQuery(
      getAgencyFilterDataQuery(getFilter(), limit),
      {
        variables: {
          filter: getFilter(),
          first: limit
        },
        skip: skipRequest,
        ssr: false,
        fetchPolicy: 'network-only'
      }
    );

    const result = data?.itemsConnection?.edges || [];
    const totalCount = data?.itemsConnection?.totalCount;
    const isShowButton =
      data?.itemsConnection?.edges.length === 4 && totalCount > 4;
    const resultBlockItems =
      result.map(item => (
        <ItemCard
          key={item.node.id}
          itemId={itemId}
          setCarousel={setCarousel}
          carousel={true}
          {...item.node}
        />
      )) || [];

    useEffect(() => {
      if (error) dispatch(setError(true));
    }, [error]);

    const placeholder = (
      <div className="agency-filter__results-block">
        <ItemCardPlaceholder />
        <ItemCardPlaceholder />
        <ItemCardPlaceholder />
        <ItemCardPlaceholder />
      </div>
    );

    if (totalCount === 0) {
      return null;
    }

    if (loading) {
      return placeholder;
    }

    return (
      <InView
        as="div"
        onChange={inView => {
          if (inView) {
            setSkipRequest(false);
          }
        }}
      >
        <div className="agency-filter__results-block">
          {
            <span className="agency-filter__results-block__header">
              {title}
            </span>
          }
          <EndlessSearchItems
            items={result || []}
            loading={loading}
            ref={filterResultsBlockRef}
            isStopLoad={
              !data?.itemsConnection.pageInfo.hasNextPage || limit === 4
            }
            onLoadMore={() => {
              const moreSearchItemsQueryVariables = {
                filter: getFilter(),
                first: limit,
                cursor: data?.itemsConnection.pageInfo.endCursor
              };

              fetchMore({
                variables: moreSearchItemsQueryVariables
              });
            }}
          >
            {resultBlockItems}
          </EndlessSearchItems>
          {isShowButton && (
            <Button
              text={t(
                setSuffix(i18n, totalCount, 'pages.agency.show_all_results'),
                {
                  n: totalCount
                }
              )}
              onClickHandler={showAllResultHandler}
              additionalClass="agency-filter__results-button"
            />
          )}
        </div>
      </InView>
    );
  }
);

AgencyFilterResultsBlock.displayName = `AgencyFilterResultsBlock`;

AgencyFilterResultsBlock.propTypes = {
  title: PropTypes.string,
  category: PropTypes.object,
  agencyId: PropTypes.string,
  activeCategoryId: PropTypes.string,
  isLeased: PropTypes.bool,
  limit: PropTypes.number,
  onClickItem: PropTypes.func,
  setActiveLeasedCategory: PropTypes.func
};

export default AgencyFilterResultsBlock;
